/**
===================================================
Module: frontend.src.components.Projects.tsx
Description: Projects management page component
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Project, CreateProjectRequest } from "../types";
import { apiService } from "../services/api";
import TopBar from "./TopBar";
import CreateProjectModal from "./CreateProjectModal";
import EditProjectModal from "./EditProjectModal";
import "../styles/Projects.css";

const Projects: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Add new state for delete functionality
  const [showMenuDropdown, setShowMenuDropdown] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(
    new Set(),
  );
  const [isDeleting, setIsDeleting] = useState(false);

  // Add new state for edit functionality
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setIsLoading(true);
      const projectsData = await apiService.getProjects();
      setProjects(projectsData);
    } catch (error) {
      setError("Failed to load projects");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProject = async (projectData: CreateProjectRequest) => {
    try {
      setIsUploading(true);
      await apiService.createProject(projectData);
      setShowCreateForm(false);
      loadProjects();
    } catch (error) {
      setError("Failed to create project");
    } finally {
      setIsUploading(false);
    }
  };

  const handleProjectClick = (projectId: string) => {
    navigate(`/projects/${projectId}/sessions`);
  };

  const handleMenuClick = () => {
    setShowMenuDropdown(!showMenuDropdown);
  };

  const handleDeleteProjects = () => {
    setIsSelectionMode(true);
    setShowMenuDropdown(false);
    // Remove automatic selection - start with empty selection
    setSelectedProjects(new Set());
  };

  const handleCancelSelection = () => {
    setIsSelectionMode(false);
    setSelectedProjects(new Set());
  };

  const handleProjectSelect = (projectId: string, isSelected: boolean) => {
    const newSelected = new Set(selectedProjects);
    if (isSelected) {
      newSelected.add(projectId);
    } else {
      newSelected.delete(projectId);
    }
    setSelectedProjects(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedProjects.size === 0) return;

    const confirmed = window.confirm(
      `フォルダを${selectedProjects.size}件削除してもよろしいですか? この操作は元に戻せません`,
    );

    if (!confirmed) return;

    try {
      setIsDeleting(true);
      const projectIds = Array.from(selectedProjects);

      // Delete projects one by one
      for (const projectId of projectIds) {
        await apiService.deleteProject(projectId);
      }

      // Clear selection and refresh data
      setSelectedProjects(new Set());
      setIsSelectionMode(false);
      loadProjects();
    } catch (error) {
      setError("Failed to delete projects. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  // Add edit project function
  const handleEditProject = (project: Project) => {
    setEditingProject(project);
    setShowEditModal(true);
  };

  const handleUpdateProject = async (name: string, description: string) => {
    if (!editingProject) return;

    try {
      setIsUpdating(true);
      await apiService.updateProject(editingProject.id, { name, description });

      // Refresh projects list
      await loadProjects();

      // Show success notification
      setError(""); // Clear any existing errors
    } catch (error) {
      setError("Failed to update project");
      throw error; // Re-throw to let the modal handle it
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingProject(null);
    setError(""); // Clear any errors
  };

  // Simple function to sort projects by creation time (newest first) - default sorting
  const getSortedProjects = () => {
    return [...projects].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  };

  // Filter projects based on search query (keep existing search functionality)
  const filteredProjects = getSortedProjects().filter(
    (project) =>
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (project.description &&
        project.description.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  if (isLoading) {
    return (
      <div className="projects-container">
        <div className="projects-content">
          <TopBar />
          <div
            style={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            フォルダのロード中...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="projects-container">
      <div className="projects-content">
        <TopBar />

        <div className="project-info">
          <div className="breadcrumb">
            <span className="breadcrumb-link">フォルダ一覧</span>
          </div>

          <div className="project-header">
            <div>
              <h1 className="project-title">ホーム</h1>
            </div>

            <div className="header-actions">
              <button
                className="add-project-btn"
                onClick={() => setShowCreateForm(true)}
                disabled={isUploading || isSelectionMode}
              >
                <span>+</span>
                フォルダの追加
              </button>

              {isSelectionMode && selectedProjects.size > 0 && (
                <button
                  className="delete-projects-btn"
                  onClick={handleBulkDelete}
                  disabled={isDeleting}
                >
                  {isDeleting
                    ? "削除中..."
                    : `${selectedProjects.size}件のフォルダを削除`}
                </button>
              )}

              <div className="menu-container">
                <div className="menu-icon" onClick={handleMenuClick}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                      fill="currentColor"
                    />
                    <path
                      d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z"
                      fill="currentColor"
                    />
                    <path
                      d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
                {showMenuDropdown && (
                  <div className="menu-dropdown">
                    <button
                      className="menu-item"
                      onClick={handleDeleteProjects}
                    >
                      フォルダを削除
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="main-content">
          {error && <div className="error-message">{error}</div>}

          <div className="search-section">
            <div className="search-container">
              <div className="search-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <input
                type="text"
                className="search-input"
                placeholder="フォルダを検索"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="clear-search-btn"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            </div>
            {searchQuery && (
              <div className="search-results-info">
                {projects.length}件中{filteredProjects.length}件のフォルダを表示
              </div>
            )}
          </div>

          {/* Move selection header here, outside of projects-section */}
          {isSelectionMode && (
            <div className="selection-mode-header">
              <div className="selection-info">
                <span className="selection-count">
                  フォルダ選択中（{selectedProjects.size}個）
                </span>
              </div>
              <div className="selection-actions">
                <button
                  className="select-all-btn"
                  onClick={() =>
                    setSelectedProjects(
                      new Set(projects.map((project) => project.id)),
                    )
                  }
                >
                  すべて選択
                </button>
                <button
                  className="deselect-all-btn"
                  onClick={() => setSelectedProjects(new Set())}
                >
                  すべて解除
                </button>
                <button
                  className="cancel-selection-btn"
                  onClick={handleCancelSelection}
                >
                  キャンセル
                </button>
              </div>
            </div>
          )}

          <div className="projects-section">
            <div className="projects-list">
              {filteredProjects.map((project) => (
                <div
                  key={project.id}
                  className={`project-card ${isSelectionMode ? "selection-mode" : ""}`}
                  onClick={() =>
                    !isSelectionMode && handleProjectClick(project.id)
                  }
                >
                  {isSelectionMode && (
                    <input
                      type="checkbox"
                      className="project-checkbox"
                      checked={selectedProjects.has(project.id)}
                      onChange={(e) =>
                        handleProjectSelect(project.id, e.target.checked)
                      }
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}

                  {/* Add edit button */}
                  {!isSelectionMode && (
                    <button
                      className="edit-project-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditProject(project);
                      }}
                      title="フォルダの編集"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M18.5 2.50023C18.8978 2.10243 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.10243 21.5 2.50023C21.8978 2.89804 22.1213 3.43762 22.1213 4.00023C22.1213 4.56285 21.8978 5.10243 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}

                  <div className="project-info">
                    <div className="project-header">
                      <h3 className="project-name">{project.name}</h3>
                    </div>

                    <p className="project-description">
                      {project.description || "説明なし"}
                    </p>

                    <div className="project-meta">
                      <div className="project-date">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>
                          {new Date(project.created_at).toLocaleDateString(
                            "ja-JP",
                          )}
                        </span>
                      </div>
                      <div className="project-time">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 12 22C6.48 22 2 17.52 2 12C2 6.48 6.48 2 12 2ZM12 6V12L16 14"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>
                          {new Date(project.created_at).toLocaleTimeString(
                            "ja-JP",
                            { hour: "2-digit", minute: "2-digit" },
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredProjects.length === 0 && !isLoading && (
              <div className="empty-state">
                {searchQuery
                  ? "条件に一致するフォルダは見つかりませんでした"
                  : "フォルダがありません"}
              </div>
            )}
          </div>
        </div>
      </div>

      <CreateProjectModal
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        onSubmit={handleCreateProject}
        isUploading={isUploading}
      />

      <EditProjectModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        onSubmit={handleUpdateProject}
        projectName={editingProject?.name || ""}
        projectDescription={editingProject?.description || ""}
        isUpdating={isUpdating}
      />
    </div>
  );
};

export default Projects;
